import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc } from 'firebase/firestore';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Header from '../components/Header';
import { auth, db } from '../firebase';
import {
  extractTextFromFile,
  getAcceptedDocumentTypes,
  isAcceptedDocumentFile,
  mergeDocumentSources,
} from '../helpers/documentTextExtract';
import { extractQuoteFromDocument } from '../helpers/extractQuoteFromDocument';
import {
  buildPrefilledQuoteForStepOne,
  normalizeExtractedQuote,
} from '../helpers/normalizeExtractedQuote';
import { blastPartyPaper } from '../helpers/partyPaperCelebration';

const QuoteFromDocument = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [salesRepName, setSalesRepName] = useState('');
  const [pastedText, setPastedText] = useState('');
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState('upload');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setSalesRepName(userDoc.data().name || user.displayName || '');
        } else {
          setSalesRepName(user.displayName || '');
        }
      } catch (err) {
        console.error(err);
        setSalesRepName(user.displayName || '');
      }
    };
    loadProfile();
  }, [user]);

  const openFullQuoteForm = useCallback((normalized, sourceText) => {
    const prefilled = buildPrefilledQuoteForStepOne(normalized);
    navigate('/create-request', {
      state: {
        prefilledQuote: {
          ...prefilled,
          salesRepName,
          fromDocumentImport: true,
        },
        importMeta: {
          summary: normalized.summary,
          missingFields: normalized.missingFields,
          notes: normalized.notes,
          sourceText,
        },
      },
    });
  }, [navigate, salesRepName]);

  const runExtraction = useCallback(async ({ text, additionalNotes, previousDraft }) => {
    setPhase('extracting');
    setError('');
    try {
      if (!user?.uid) {
        throw new Error('You must be logged in to import a quote.');
      }

      const raw = await extractQuoteFromDocument({
        text,
        additionalNotes,
        previousDraft,
      });
      const normalized = normalizeExtractedQuote(raw, { sourceText: text });
      blastPartyPaper();
      toast.success('Fields extracted — review the form and click Submit Quote when ready.');
      openFullQuoteForm(normalized, text);
    } catch (err) {
      setError(err.message);
      setPhase('upload');
      toast.error(err.message);
    }
  }, [openFullQuoteForm, user]);

  const handleExtract = async () => {
    try {
      const fileTexts = await Promise.all(files.map((file) => extractTextFromFile(file)));
      const combined = mergeDocumentSources(pastedText, fileTexts);
      if (!combined.trim()) {
        toast.error('Paste text or upload at least one file.');
        return;
      }
      await runExtraction({ text: combined });
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    }
  };

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    const valid = selected.filter(isAcceptedDocumentFile);
    if (valid.length !== selected.length) {
      toast.error('Only .txt, .docx, and .pdf files are supported.');
    }
    setFiles(valid);
  };

  return (
    <>
      <Header />
      <div className="parent-container pt-5 h-100 pb-10 p-4 sm:ml-64">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Import Quote from Document</h1>
          <p className="text-gray-600 mb-6">
            Paste customer text or upload PDF / Word files. We detect the product type (Bags, Boxes,
            Labels, etc.) and open the <strong>full quote form</strong> with matching fields
            pre-filled. Fill in anything missing, then click <strong>Submit Quote</strong> to save.
          </p>

          {phase === 'upload' && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Paste email, RFQ, or notes
                </label>
                <textarea
                  value={pastedText}
                  onChange={(e) => setPastedText(e.target.value)}
                  rows={8}
                  placeholder="Paste any customer message, spec, or quote request here..."
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  Or upload files (.txt, .docx, .pdf)
                </label>
                <input
                  type="file"
                  multiple
                  accept={getAcceptedDocumentTypes()}
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-900 file:text-white hover:file:bg-blue-700"
                />
                {files.length > 0 && (
                  <p className="mt-2 text-sm text-gray-500">
                    {files.length} file(s): {files.map((f) => f.name).join(', ')}
                  </p>
                )}
              </div>

              {error && <p className="text-red-600 text-sm bg-red-50 rounded-lg p-3">{error}</p>}

              <button
                type="button"
                onClick={handleExtract}
                className="px-6 py-3 bg-blue-900 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Extract and open quote form
              </button>
            </div>
          )}

          {phase === 'extracting' && (
            <div className="py-16 text-center text-gray-600">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p>Detecting product type and mapping to the full quote form…</p>
            </div>
          )}
        </div>
      </div>
      <ToastContainer position="top-center" autoClose={5000} />
    </>
  );
};

export default QuoteFromDocument;
